import React, { useCallback, useEffect, useState } from 'react'
import { ErrorCode } from '@linqpal/models/src/types/exceptions'
import { commonColors } from '@linqpal/common-frontend/src/theme'
import { Text } from 'react-native-paper'
import Layout from '../../Auth/Layout'
import Spacer from '../../../ui/atoms/Spacer'
import OtpInput from '../../../ui/atoms/OtpInput'
import { Link } from '../../../ui/atoms/Link'
import Title, { SubTitle } from '../../Auth/Title'
import SubmitButton from '../../Auth/SubmitButton'
import RootStore from '../../../store/RootStore'
import { observer, Observer } from 'mobx-react'
import BackButton from '../../Auth/BackButton'
import { useTranslation } from 'react-i18next'
import { ErrorMessage } from '../../Auth/ErrorMessage'
import useRecaptcha from '../../Auth/useRecaptcha'

export type onNextPayload = {
  otp: string
  codeVerifier: any
}
interface SignInVerifyProps {
  onNext: (payload: onNextPayload) => void
  onBack: () => void
  onSignUp: () => void
  onResendSmsCode: () => void
  login: string
  loading: boolean
  error?: ErrorCode | false
}

const SignInVerify: React.FC<SignInVerifyProps> = observer(
  ({ onNext, onBack, onSignUp, onResendSmsCode, login, loading, error }) => {
    const [otp, setOtp] = useState('')
    const { t } = useTranslation('auth')
    const { verifier, createVerifier } = useRecaptcha('verify-phone')
    useEffect(() => {
      createVerifier()
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const handleNext = useCallback(() => {
      onNext({ otp, codeVerifier: verifier })
    }, [onNext, otp, verifier])

    const handleChange = useCallback(
      (code: string) => {
        setOtp(code)
      },
      [setOtp],
    )
    return (
      <Layout onBackPress={onBack}>
        <BackButton onPress={onBack} testID="back-button-to-login" />
        <Title testID="code-title">{t('codeTitle')}</Title>
        <Spacer height={16} />
        <SubTitle testID="code-subtitle">
          {t('codeSubTitle')}
          <Text style={{ color: commonColors.accentText }}>
            ****{login.slice(-4)}
          </Text>
        </SubTitle>
        <Spacer height={40} />
        <OtpInput
          otp={otp}
          onChange={handleChange}
          testID="six-digit-code-component"
        />
        <Spacer />
        <ErrorMessage error={error} />
        <Spacer height={20} />
        <Text
          style={{
            fontSize: 14,
            color: commonColors.textLight,
            marginVertical: 20,
            textAlign: 'center',
          }}
          testID="codeQuestion"
        >
          {t('codeQuestion')}
          <Link
            title={t('resendCode')}
            color="#00A0F3"
            onPress={onResendSmsCode}
            testID="resend-code-link"
          />
        </Text>
        <Observer>
          {() => (
            <SubmitButton
              disabled={loading || otp.length !== 6}
              onPress={handleNext}
              testID={'sign-in-submit-btn'}
              loading={RootStore.isBusy}
            >
              {t('signIn.loginButtonLabel')}
            </SubmitButton>
          )}
        </Observer>
        <Text
          style={{
            color: commonColors.textLight,
            marginTop: 40,
            fontWeight: '500',
            fontSize: 16,
          }}
          testID="signup-question"
        >
          {t('signIn.signUpQuestion')}
          <Link
            textStyle={{ fontWeight: '500', fontSize: 16 }}
            title={t('SignUp')}
            color="#00A0F3"
            onPress={onSignUp}
            testID="signup-link"
          />
        </Text>
        <Spacer />
      </Layout>
    )
  },
)

export default SignInVerify
